import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Analytics.css";
import { 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  BarChart3,
  PieChart,
  TrendingUp,
  Target,
  Zap,
  Eye,
  MousePointer,
  LineChart,
  Activity,
  Layers,
  GitBranch,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  Sparkles,
  Settings,
  Database
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// ============================================
// ANALYTICS PAGE VISUALIZATIONS
// ============================================

// Hero Visualization - Attribution Dashboard
const AnalyticsHeroVisualization = () => {
  const channels = [
    { name: 'Email', value: 50, color: '#3b82f6' },
    { name: 'Paid Search', value: 30, color: '#8b5cf6' },
    { name: 'Social', value: 20, color: '#10b981' },
    { name: 'Display', value: 10, color: '#f59e0b' },
    { name: 'Direct', value: 5, color: '#ec4899' },
  ];

  return (
    <div className="analytics-hero-viz">
      <div className="analytics-viz-header">
        <span className="analytics-viz-label">REAL-TIME ACTIVATION</span>
        <Settings size={14} className="settings-icon" />
      </div>
      
      <div className="attribution-bars">
        {channels.map((channel, i) => (
          <div key={i} className="attr-bar-row">
            <div className="attr-checkbox"></div>
            <span className="attr-value">{channel.value}%</span>
            <div className="attr-bar-track">
              <div 
                className="attr-bar-fill" 
                style={{ width: `${channel.value}%`, backgroundColor: channel.color }}
              ></div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="measurement-label">
        <span>Measurement</span>
      </div>
    </div>
  );
};

// Analytics Dashboard Visualization
const DashboardVisualization = () => {
  const metrics = [
    { label: 'Impressions', value: '2.4M', change: '+12%' },
    { label: 'Clicks', value: '89K', change: '+8%' },
    { label: 'Conversions', value: '4.2K', change: '+24%' },
    { label: 'Revenue', value: '$847K', change: '+18%' },
  ];

  return (
    <div className="analytics-viz-container dashboard-viz">
      <div className="analytics-viz-header">
        <span className="analytics-viz-label">PERFORMANCE DASHBOARD</span>
        <span className="analytics-viz-badge">REAL-TIME</span>
      </div>
      
      <div className="dashboard-metrics">
        {metrics.map((metric, i) => (
          <div key={i} className="dash-metric">
            <span className="dash-label">{metric.label}</span>
            <span className="dash-value">{metric.value}</span>
            <span className="dash-change positive">{metric.change}</span>
          </div>
        ))}
      </div>
      
      <div className="dashboard-chart">
        <svg viewBox="0 0 200 80" className="dash-chart-svg">
          <defs>
            <linearGradient id="dashGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
            </linearGradient>
          </defs>
          
          {/* Grid lines */}
          {[20, 40, 60].map((y, i) => (
            <line key={i} x1="0" y1={y} x2="200" y2={y} stroke="#e2e8f0" strokeWidth="0.5"/>
          ))}
          
          {/* Area chart */}
          <path d="M0,60 L25,55 L50,45 L75,50 L100,35 L125,40 L150,25 L175,30 L200,15 L200,80 L0,80 Z" 
                fill="url(#dashGrad)"/>
          <path d="M0,60 L25,55 L50,45 L75,50 L100,35 L125,40 L150,25 L175,30 L200,15" 
                fill="none" stroke="#3b82f6" strokeWidth="2"/>
          
          {/* Data points */}
          {[[0,60], [50,45], [100,35], [150,25], [200,15]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3" fill="#3b82f6"/>
          ))}
        </svg>
      </div>
      
      <div className="dashboard-features">
        <div className="dash-feat">
          <BarChart3 size={14} />
          <span>Custom Reports</span>
        </div>
        <div className="dash-feat">
          <Activity size={14} />
          <span>Live Tracking</span>
        </div>
        <div className="dash-feat">
          <Clock size={14} />
          <span>Historical Data</span>
        </div>
      </div>
    </div>
  );
};

// Heatmap Visualization
const HeatmapVisualization = () => {
  const heatData = [
    [0.9, 0.7, 0.5, 0.3, 0.6, 0.8, 0.4],
    [0.6, 0.8, 0.9, 0.5, 0.3, 0.7, 0.6],
    [0.4, 0.5, 0.7, 0.9, 0.8, 0.4, 0.5],
    [0.3, 0.4, 0.6, 0.8, 0.9, 0.6, 0.7],
    [0.5, 0.6, 0.4, 0.5, 0.7, 0.9, 0.8],
  ];

  const getColor = (val) => {
    const r = Math.round(59 + (val * 100));
    const g = Math.round(130 - (val * 50));
    const b = Math.round(246 - (val * 100));
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className="analytics-viz-container heatmap-viz">
      <div className="analytics-viz-header">
        <span className="analytics-viz-label">ENGAGEMENT HEATMAP</span>
        <span className="analytics-viz-badge green">ACTIVE</span>
      </div>
      
      <div className="heatmap-grid">
        {heatData.map((row, i) => (
          <div key={i} className="heatmap-row">
            {row.map((val, j) => (
              <div 
                key={j} 
                className="heatmap-cell"
                style={{ backgroundColor: getColor(val), opacity: 0.5 + val * 0.5 }}
              ></div>
            ))}
          </div>
        ))}
      </div>
      
      <div className="heatmap-legend">
        <span>Low</span>
        <div className="legend-gradient"></div>
        <span>High</span>
      </div>
      
      <div className="heatmap-insights">
        <div className="insight-item">
          <Eye size={14} />
          <span>Peak: Tue 2-4pm</span>
        </div>
        <div className="insight-item">
          <MousePointer size={14} />
          <span>CTR: 12.4%</span>
        </div>
      </div>
    </div>
  );
};

// Insights Studio Visualization
const InsightsStudioVisualization = () => {
  const datasets = [
    { name: 'Campaign Performance', status: 'ready' },
    { name: 'Customer Journeys', status: 'ready' },
    { name: 'Channel Attribution', status: 'building' },
    { name: 'Revenue Trends', status: 'ready' },
  ];

  return (
    <div className="analytics-viz-container insights-viz">
      <div className="analytics-viz-header">
        <span className="analytics-viz-label">INSIGHTS STUDIO</span>
        <span className="analytics-viz-badge purple">WORKSPACE</span>
      </div>
      
      <div className="insights-datasets">
        {datasets.map((ds, i) => (
          <div key={i} className={`dataset-row ${ds.status}`}>
            <Database size={14} />
            <span className="dataset-name">{ds.name}</span>
            <span className={`dataset-status ${ds.status}`}>
              {ds.status === 'ready' ? 'Ready' : 'Building...'}
            </span>
          </div>
        ))}
      </div>
      
      <div className="insights-preview">
        <div className="preview-chart">
          <div className="mini-bar" style={{ height: '60%' }}></div>
          <div className="mini-bar" style={{ height: '80%' }}></div>
          <div className="mini-bar" style={{ height: '45%' }}></div>
          <div className="mini-bar" style={{ height: '90%' }}></div>
          <div className="mini-bar" style={{ height: '70%' }}></div>
        </div>
      </div>
      
      <div className="insights-features">
        <div className="insight-feat">
          <Layers size={14} />
          <span>Ad-hoc Analysis</span>
        </div>
        <div className="insight-feat">
          <Sparkles size={14} />
          <span>Templates</span>
        </div>
      </div>
    </div>
  );
};

// Attribution Model Visualization
const AttributionVisualization = () => {
  const touchpoints = [
    { channel: 'Email', attribution: 35, conversions: '1.2K' },
    { channel: 'Paid Search', attribution: 28, conversions: '980' },
    { channel: 'Social', attribution: 18, conversions: '640' },
    { channel: 'Display', attribution: 12, conversions: '420' },
    { channel: 'Direct', attribution: 7, conversions: '245' },
  ];

  return (
    <div className="analytics-viz-container attribution-viz">
      <div className="analytics-viz-header">
        <span className="analytics-viz-label">MULTI-TOUCH ATTRIBUTION</span>
        <span className="analytics-viz-badge">AI-POWERED</span>
      </div>
      
      <div className="attribution-table">
        <div className="attr-table-header">
          <span>Channel</span>
          <span>Attribution</span>
          <span>Conversions</span>
        </div>
        {touchpoints.map((tp, i) => (
          <div key={i} className="attr-table-row">
            <span className="channel-name">{tp.channel}</span>
            <div className="attr-bar-mini">
              <div className="attr-fill-mini" style={{ width: `${tp.attribution}%` }}></div>
              <span>{tp.attribution}%</span>
            </div>
            <span className="conversions">{tp.conversions}</span>
          </div>
        ))}
      </div>
      
      <div className="attribution-footer">
        <div className="attr-total">
          <DollarSign size={14} />
          <span>$847K attributed revenue</span>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const AnalyticsHero = () => {
  return (
    <section className="analytics-hero">
      <div className="analytics-hero-content">
        <div className="analytics-hero-text">
          <span className="analytics-label">ANALYTICS & ATTRIBUTION</span>
          <h1>Measurable Outcomes. <span className="highlight">Clear Attribution.</span></h1>
          <p>
            Our analytics platform combines proprietary consumer data with 
            campaign performance signals to provide comprehensive visibility 
            into what's driving results and where to optimize.
          </p>
          <div className="analytics-hero-cta">
            <Link to="/contact">
              <Button className="btn-primary-zeta">
                Explore Capabilities
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
        <div className="analytics-hero-visual">
          <AnalyticsHeroVisualization />
        </div>
      </div>
    </section>
  );
};

// Intro Section
const AnalyticsIntro = () => {
  return (
    <section className="analytics-intro">
      <div className="analytics-intro-content">
        <span className="analytics-intro-label">Data-Driven Decisions</span>
        <p>
          Understand what drives <strong>every conversion</strong>. Our analytics 
          combine proprietary consumer signals with campaign data to reveal 
          actionable insights for continuous optimization.
        </p>
      </div>
    </section>
  );
};

// Feature Section Component
const AnalyticsFeatureSection = ({ 
  title, 
  description, 
  bullets, 
  visualization: Visualization,
  reversed = false
}) => {
  return (
    <section className={`analytics-feature-section ${reversed ? 'reversed' : ''}`}>
      <div className="analytics-feature-wrapper">
        <div className="analytics-feature-visual">
          <Visualization />
        </div>
        <div className="analytics-feature-text">
          <h2>{title}</h2>
          <p className="analytics-feature-desc">{description}</p>
          <div className="analytics-bullet-list">
            {bullets.map((bullet, index) => (
              <div key={index} className="analytics-bullet-item">
                <div className="analytics-bullet-bar"></div>
                <p>{bullet}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const AnalyticsFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "How does IntentLogic handle analytics for my campaigns?",
      answer: "We tie actions to real people—not proxies. Our Identity Graph ensures accurate attribution, better segmentation, and higher confidence in ROI reporting for every campaign we execute."
    },
    {
      question: "How is your AI-powered attribution different from legacy tools?",
      answer: "Legacy tools rely on last-click or siloed views. We use AI-powered attribution to track full-funnel journeys, revealing which messages and channels truly drive performance for your brand."
    },
    {
      question: "How do you help reduce wasted spend?",
      answer: "We identify underperforming spend and re-route your budget toward high-impact touchpoints. Our team optimizes your investments in real time so every dollar works harder."
    },
    {
      question: "What role does AI play in your analytics services?",
      answer: "AI accelerates our analytics by detecting patterns and predicting outcomes faster than traditional methods. We apply these insights in real time, helping your brand outpace competitors."
    }
  ];

  return (
    <section className="analytics-faq-section">
      <h2>FAQs</h2>
      <div className="analytics-faq-list">
        {faqs.map((faq, index) => (
          <div 
            key={index} 
            className={`analytics-faq-item ${openIndex === index ? 'open' : ''}`}
          >
            <button 
              className="analytics-faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.question}</span>
              {openIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openIndex === index && (
              <div className="analytics-faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const AnalyticsCTA = () => (
  <section className="analytics-cta-section" id="analytics-cta">
    <div className="analytics-cta-content">
      <h2>Let Us Handle Your Analytics</h2>
      <p>
        Partner with IntentLogic. We manage your analytics, track every touchpoint, 
        and deliver the insights you need—so you can focus on growing your business.
      </p>
      <Link to="/contact">
        <Button className="btn-primary-zeta">
          Schedule Consultation
          <ArrowRight size={18} />
        </Button>
      </Link>
    </div>
  </section>
);

// Main Analytics Page Component
function AnalyticsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEO 
        title="Analytics & Attribution - Marketing Performance Intelligence"
        description="Our analytics platform combines proprietary consumer data with campaign performance signals to provide comprehensive visibility into what's driving results and where to optimize."
        keywords="marketing analytics, attribution modeling, campaign analytics, performance measurement, marketing ROI, data visualization, cross-channel attribution, marketing intelligence, conversion tracking"
        canonical="/analytics"
      />
      <AnalyticsHero />
      <AnalyticsIntro />
      
      <AnalyticsFeatureSection
        title="Unified Performance Visibility"
        description="Our analytics platform aggregates data across channels and campaigns to provide comprehensive performance visibility in real time."
        bullets={[
          "Real-time dashboards with customizable KPI tracking.",
          "Cross-channel performance comparison and analysis.",
          "Automated alerts for performance anomalies and opportunities."
        ]}
        visualization={DashboardVisualization}
      />
      
      <AnalyticsFeatureSection
        title="Engagement Analysis & Optimization"
        description="Detailed engagement analytics reveal which content, timing, and channels drive the strongest response rates."
        bullets={[
          "Engagement heat maps showing content and link performance.",
          "Send time optimization based on historical response patterns.",
          "A/B test analysis with statistical significance reporting."
        ]}
        visualization={HeatmapVisualization}
        reversed
      />
      
      <AnalyticsFeatureSection
        title="Self-Service Insights Studio"
        description="Our analytics workspace enables rapid exploration of campaign data with pre-built templates and custom report creation."
        bullets={[
          "Drag-and-drop report builder with extensive data catalog.",
          "Pre-built templates for common analysis scenarios.",
          "Export capabilities for stakeholder presentations."
        ]}
        visualization={InsightsStudioVisualization}
      />
      
      <AnalyticsFeatureSection
        title="Advanced Attribution & Modeling"
        description="Multi-touch attribution and incrementality analysis provide clear understanding of channel contribution and true campaign impact."
        bullets={[
          "Multi-touch attribution across the full customer journey.",
          "Incrementality testing to measure true campaign lift.",
          "Custom modeling for complex attribution requirements."
        ]}
        visualization={AttributionVisualization}
        reversed
      />
      
      <AnalyticsFAQ />
      <AnalyticsCTA />
    </Layout>
  );
}

export default AnalyticsPage;
